import React, { useEffect, useState } from "react";
import {
  Table, Button, Tag, Space, Switch, Modal, Descriptions,
  message, Tooltip, Select, Typography, Statistic, Row, Col, Card,
} from "antd";
import {
  EditOutlined, BarChartOutlined, LinkOutlined, PoweroffOutlined,
} from "@ant-design/icons";
import useApi from "../../../hooks/useApi";
import { API_METHODS } from "../../../constants";
import { isEmpty } from "../../../generalFunctions";

const { Text } = Typography;
const { Option } = Select;

const SITE_BASE = process.env.NODE_ENV === "development"
  ? "http://localhost:3000"
  : (window.location.origin.includes("admin.")
      ? window.location.origin.replace("admin.", "")
      : window.location.origin);

function EventList({ refreshKey, onEdit, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [statsModal, setStatsModal] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const [listResponse, listRequest] = useApi("/admin/events", null, API_METHODS.GET);
  const [toggleResponse, toggleRequest] = useApi(null, null, API_METHODS.PATCH);
  const [statsResponse, statsRequest] = useApi(null, null, API_METHODS.GET);

  useEffect(() => {
    listRequest(null, API_METHODS.GET, `/admin/events?status=${statusFilter}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, statusFilter]);

  useEffect(() => {
    if (!isEmpty(toggleResponse) && !toggleResponse.isLoading) {
      if (toggleResponse.data) {
        messageApi.success(toggleResponse.data.message || "Status updated");
        onRefresh();
      }
      if (toggleResponse.error) messageApi.error(toggleResponse.error);
    }
  }, [toggleResponse, messageApi, onRefresh]);

  const handleToggle = (id) => {
    toggleRequest(null, API_METHODS.PATCH, `/admin/events/${id}/toggle`);
  };

  const handleViewStats = (id) => {
    setStatsModal({ id, loading: true });
    statsRequest(null, API_METHODS.GET, `/admin/events/${id}/stats`);
  };

  useEffect(() => {
    if (!isEmpty(statsResponse) && !statsResponse.isLoading) {
      if (statsResponse.data) {
        setStatsModal((prev) => prev ? { ...prev, data: statsResponse.data, loading: false } : null);
      }
    }
  }, [statsResponse]);

  const copyUrl = (slug) => {
    const url = `${SITE_BASE}/event/${slug}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => messageApi.success("URL copied!"));
    } else {
      // Fallback for non-HTTPS (e.g. local dev)
      const el = document.createElement("textarea");
      el.value = url;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      messageApi.success("URL copied!");
    }
  };

  const isExpired = (endDate) => new Date(endDate) < new Date(new Date().setHours(0, 0, 0, 0));

  const columns = [
    {
      title: "Event",
      dataIndex: "heading",
      render: (text, record) => (
        <div>
          <div className="font-semibold">{text}</div>
          {record.sub_heading && <div className="text-xs text-gray-500">{record.sub_heading}</div>}
        </div>
      ),
    },
    {
      title: "Dates",
      render: (_, r) => (
        <div>
          <div>{r.start_date}</div>
          {r.start_date !== r.end_date && <div className="text-xs text-gray-500">to {r.end_date}</div>}
        </div>
      ),
    },
    {
      title: "Packages",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.price_single && <Text>Single: ₹{r.price_single}</Text>}
          {r.price_couple && <Text>Couple: ₹{r.price_couple}</Text>}
          {r.price_family && <Text>Family: ₹{r.price_family}</Text>}
        </Space>
      ),
    },
    {
      title: "Bookings",
      dataIndex: "total_bookings",
      align: "center",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "Status",
      render: (_, r) => (
        <Space>
          {isExpired(r.end_date) ? (
            <Tag color="default">Expired</Tag>
          ) : (
            <Tag color={r.is_active ? "green" : "orange"}>
              {r.is_active ? "Active" : "Inactive"}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "URL",
      render: (_, r) => (
        <Tooltip title={`${SITE_BASE}/event/${r.slug}`}>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={() => copyUrl(r.slug)}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
          >
            Copy
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      render: (_, r) => {
        const isExpired = new Date(r.end_date) < new Date(new Date().toDateString());
        return (
        <Space>
          <Tooltip title={isExpired ? "Cannot edit an expired event" : ""}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(r)}
              disabled={isExpired}
              style={isExpired ? {} : { backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
            >
              Edit
            </Button>
          </Tooltip>
          <Button
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => handleViewStats(r.id)}
            style={{ backgroundColor: "#1677ff", borderColor: "#1677ff", color: "#fff" }}
          >
            Stats
          </Button>
          <Tooltip title={r.is_active ? "Deactivate" : "Activate"}>
            <Switch
              size="small"
              checked={!!r.is_active}
              onChange={() => handleToggle(r.id)}
              loading={toggleResponse?.isLoading}
            />
          </Tooltip>
        </Space>
        );
      },
    },
  ];

  const events = listResponse?.data || [];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16 }}>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
        >
          <Option value="all">All Events</Option>
          <Option value="active">Active</Option>
          <Option value="expired">Expired / Inactive</Option>
        </Select>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={events}
        loading={listResponse?.isLoading}
        pagination={{ pageSize: 10 }}
      />

      {/* Stats Modal */}
      <Modal
        open={!!statsModal}
        title={statsModal?.data?.event?.heading || "Event Stats"}
        onCancel={() => setStatsModal(null)}
        footer={null}
        width={700}
      >
        {statsModal?.loading ? (
          <div className="text-center py-8">Loading stats...</div>
        ) : statsModal?.data ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic title="Total Bookings" value={statsModal.data.totals?.total_bookings || 0} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Revenue"
                    value={statsModal.data.totals?.total_revenue || 0}
                    prefix="₹"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div>Single: <strong>{statsModal.data.totals?.single_count || 0}</strong></div>
                  <div>Couple: <strong>{statsModal.data.totals?.couple_count || 0}</strong></div>
                  <div>Family: <strong>{statsModal.data.totals?.family_count || 0}</strong></div>
                </Card>
              </Col>
            </Row>

            <Table
              size="small"
              rowKey={(r) => `${r.booking_date}-${r.package_type}`}
              columns={[
                { title: "Date", dataIndex: "booking_date" },
                { title: "Package", dataIndex: "package_type", render: (v) => <Tag>{v}</Tag> },
                { title: "Count", dataIndex: "count", align: "center" },
                { title: "Revenue", dataIndex: "revenue", render: (v) => `₹${v}` },
              ]}
              dataSource={statsModal.data.breakdown}
              pagination={false}
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

export default EventList;
